import React, { useState, useEffect } from 'react';
import Layout from '../../components/layout/Layout';
import presupuestoService from '../../services/presupuestoService';
import categoriaService from '../../services/categoriaService';
import { formatearMoneda } from '../../utils/helpers';
import { FaPlus, FaEdit, FaTrash, FaChartPie } from 'react-icons/fa';

const MESES = [
  'Enero','Febrero','Marzo','Abril','Mayo','Junio',
  'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'
];

const FORM_INICIAL = {
  categoria_id: '', monto_limite: '',
  anio: new Date().getFullYear(),
  mes:  new Date().getMonth() + 1,
  moneda: 'PEN',
  tipo: 'gastos',
};

const getColorEstado = (estado) => {
  const colores = {
    normal:   { bg: 'success', label: 'Normal' },
    riesgo:   { bg: 'warning', label: 'En Riesgo' },
    superado: { bg: 'danger',  label: 'Superado' },
  };
  return colores[estado] || { bg: 'secondary', label: estado };
};

export default function PresupuestosPage() {
  const anioActual = new Date().getFullYear();
  const mesActual  = new Date().getMonth() + 1;

  const [presupuestos, setPresupuestos] = useState([]);
  const [categorias,   setCategorias]   = useState([]);
  const [cargando,     setCargando]     = useState(true);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [editando,     setEditando]     = useState(null);
  const [anio,         setAnio]         = useState(anioActual);
  const [mes,          setMes]          = useState(mesActual);
  const [form,         setForm]         = useState(FORM_INICIAL);

  useEffect(() => { cargarDatos(); }, [anio, mes]);

  const cargarDatos = async () => {
    setCargando(true);
    try {
      const [pres, cat] = await Promise.all([
        presupuestoService.listar(anio, mes),
        categoriaService.listar(),
      ]);
      setPresupuestos(pres || []);
      setCategorias(cat);
    } finally {
      setCargando(false);
    }
  };

  const abrirModal = (presupuesto = null) => {
    setEditando(presupuesto);
    setForm(presupuesto ? {
      categoria_id: presupuesto.categoria?.id || '',
      monto_limite: presupuesto.monto_limite,
      anio,
      mes,
      moneda: presupuesto.moneda || 'PEN',
      tipo:   presupuesto.tipo   || 'gastos',
    } : { ...FORM_INICIAL, anio, mes });
    setMostrarModal(true);
  };

  const cerrarModal = () => {
    setMostrarModal(false);
    setEditando(null);
    setForm(FORM_INICIAL);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      editando
        ? await presupuestoService.actualizar(editando.id, { monto_limite: form.monto_limite })
        : await presupuestoService.crear(form);
      cerrarModal();
      cargarDatos();
    } catch (err) {
      console.error('Error al guardar presupuesto:', err);
    }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm('¿Eliminar este presupuesto?')) return;
    await presupuestoService.eliminar(id);
    cargarDatos();
  };

  return (
    <Layout>
      <div className="page-header d-flex justify-content-between align-items-center">
        <div>
          <h4>Presupuestos</h4>
          <p>Control de presupuestos por categoría</p>
        </div>
        <div className="d-flex gap-2">
          <select className="form-select form-select-sm" value={mes} onChange={(e) => setMes(Number(e.target.value))}>
            {MESES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <select className="form-select form-select-sm" value={anio} onChange={(e) => setAnio(Number(e.target.value))}>
            {[2024, 2025, 2026].map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <button className="btn btn-danger" onClick={() => abrirModal()}>
            <FaPlus className="me-2" /> Nuevo
          </button>
        </div>
      </div>

      {!cargando && presupuestos.length > 0 && (
        <div className="row g-3 mb-3">
          {['normal', 'riesgo', 'superado'].map((estado) => {
            const count = presupuestos.filter((p) => p.estado === estado).length;
            const color = getColorEstado(estado);
            return (
              <div key={estado} className="col-md-4">
                <div className={`card border-${color.bg} card-custom`}>
                  <div className="card-body py-3 text-center">
                    <div className={`fw-bold text-${color.bg} fs-4`}>{count}</div>
                    <div className="small text-muted">{color.label}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="card card-custom">
        <div className="card-body">
          {cargando ? (
            <div className="text-center py-4"><div className="spinner-custom mx-auto" /></div>
          ) : (
            <div className="table-responsive">
              <table className="table table-custom">
                <thead>
                  <tr>
                    <th>Categoría</th>
                    <th>Tipo</th>
                    <th>Presupuesto</th>
                    <th>Ejecutado</th>
                    <th>% Avance</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {presupuestos.length > 0 ? presupuestos.map((p) => {
                    const color = getColorEstado(p.estado);
                    return (
                      <tr key={p.id}>
                        <td className="fw-bold">{p.categoria?.nombre || '—'}</td>
                        <td>
                          <span className={`badge ${p.tipo === 'ingresos' ? 'bg-success' : 'bg-primary'}`}>
                            {p.tipo === 'ingresos' ? 'Ingresos' : 'Gastos'}
                          </span>
                        </td>
                        <td>{formatearMoneda(p.monto_limite)}</td>
                        <td className={`fw-bold text-${color.bg}`}>{formatearMoneda(p.monto_ejecutado)}</td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <div className="progress flex-grow-1" style={{ height:'8px' }}>
                              <div className={`progress-bar bg-${color.bg}`} style={{ width:`${Math.min(p.porcentaje, 100)}%` }} />
                            </div>
                            <span className="small fw-bold">{p.porcentaje}%</span>
                          </div>
                        </td>
                        <td><span className={`badge bg-${color.bg}`}>{color.label}</span></td>
                        <td>
                          <button className="btn btn-sm btn-outline-primary me-1" onClick={() => abrirModal(p)}><FaEdit /></button>
                          <button className="btn btn-sm btn-outline-danger" onClick={() => handleEliminar(p.id)}><FaTrash /></button>
                        </td>
                      </tr>
                    );
                  }) : (
                    <tr>
                      <td colSpan="7" className="text-center text-muted py-4">
                        <FaChartPie size={32} className="mb-2 d-block mx-auto" />
                        Sin presupuestos para este período
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {mostrarModal && (
        <div className="modal-overlay">
          <div className="modal-custom">
            <div className="modal-header-custom">
              <h5>{editando ? 'Editar Presupuesto' : 'Nuevo Presupuesto'}</h5>
              <button className="btn-close-custom" onClick={cerrarModal}>×</button>
            </div>
            <div className="modal-body-custom">
              <form onSubmit={handleSubmit}>
                <div className="row g-3">

                  {!editando && (
                    <div className="col-12">
                      <label className="form-label fw-bold">Categoría *</label>
                      <select name="categoria_id" className="form-select" value={form.categoria_id} onChange={handleChange} required>
                        <option value="">Seleccionar categoría</option>
                        {categorias.map((cat) => <option key={cat.id} value={cat.id}>{cat.nombre}</option>)}
                      </select>
                    </div>
                  )}

                  {!editando && (
                    <div className="col-12">
                      <label className="form-label fw-bold">Tipo *</label>
                      <select name="tipo" className="form-select" value={form.tipo} onChange={handleChange} required>
                        <option value="gastos">Gastos</option>
                        <option value="ingresos">Ingresos</option>
                      </select>
                    </div>
                  )}

                  <div className="col-md-6">
                    <label className="form-label fw-bold">Monto Límite *</label>
                    <input name="monto_limite" type="number" step="0.01" className="form-control" value={form.monto_limite} onChange={handleChange} required min="0.01" />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold">Moneda</label>
                    <select name="moneda" className="form-select" value={form.moneda} onChange={handleChange}>
                      <option value="PEN">Soles (PEN)</option>
                      <option value="USD">Dólares (USD)</option>
                    </select>
                  </div>

                </div>
                <div className="d-flex gap-2 justify-content-end mt-3">
                  <button type="button" className="btn btn-secondary" onClick={cerrarModal}>Cancelar</button>
                  <button type="submit" className="btn btn-danger">{editando ? 'Actualizar' : 'Guardar'}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
